import { useState } from 'react'
import { Building2, CheckCircle2, ChevronRight, Loader2, XCircle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Modal } from '@/components/ui'
import { useTenantStore } from '@/stores/tenants.ts'

interface QuoteStepDebugModalProps {
  open: boolean
  quoteId: string
  onClose: () => void
  onExportComplete?: () => void
}

interface StepResult {
  step: string
  errors?: Array<{ field: string; message: string }>
  error?: string
  code?: string | null
  quote?: unknown
  positions?: unknown[]
  body?: Record<string, unknown>
  cardCode?: string
  comments?: string
  request?: { method?: string; path?: string; body?: unknown }
  response?: unknown
  httpStatus?: number | null
  sapResponse?: unknown
  docEntry?: number
  docNum?: number
  ok?: boolean
}

type StepKey = 'validate' | 'build-body' | 'create' | 'save-back'

interface StepRow {
  key: StepKey
  label: string
  description: string
  pending: 'idle' | 'running' | 'done' | 'error'
  result?: StepResult
}

const INITIAL_STEPS: StepRow[] = [
  { key: 'validate', label: '1. Validieren', description: 'Pre-Flight: BP-Code, Positionen, ItemCodes existieren in SAP', pending: 'idle' },
  { key: 'build-body', label: '2. /Quotations-Body bauen', description: 'Lokal: Mappt Positionen auf DocumentLines, ohne Request', pending: 'idle' },
  { key: 'create', label: '3. POST /Quotations', description: 'Sendet das Angebot an SAP B1, gibt DocEntry/DocNum zurück', pending: 'idle' },
  { key: 'save-back', label: '4. Lokal zurückschreiben', description: 'Speichert sapDocEntry/sapDocNum + status=exported in der lokalen Quote', pending: 'idle' },
]

export function QuoteStepDebugModal({ open, quoteId, onClose, onExportComplete }: QuoteStepDebugModalProps) {
  const activeTenantId = useTenantStore((s) => s.activeTenantId)
  const tenants = useTenantStore((s) => s.tenants)
  const activeTenant = tenants.find((t) => t.id === activeTenantId)
  const [steps, setSteps] = useState<StepRow[]>(INITIAL_STEPS)
  const [createBody, setCreateBody] = useState<string>('')
  const [docEntry, setDocEntry] = useState<string>('')
  const [docNum, setDocNum] = useState<string>('')

  function updateStep(key: StepKey, patch: Partial<StepRow>) {
    setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  }

  async function runStep(key: StepKey) {
    updateStep(key, { pending: 'running', result: undefined })
    try {
      const params = new URLSearchParams({ step: key })
      let body: Record<string, unknown> = {}
      if (key === 'create') {
        body = { body: JSON.parse(createBody || '{}') }
      } else if (key === 'save-back') {
        body = { docEntry: Number(docEntry), docNum: Number(docNum) }
      }
      const res = await apiClient.post(`/quotes/${quoteId}/export-step?${params}`, body)
      const data = res.data as StepResult
      const hasError = !!data?.error || (data?.errors && data.errors.length > 0)
      updateStep(key, { pending: hasError ? 'error' : 'done', result: data })

      if (key === 'build-body' && data.body) {
        setCreateBody(JSON.stringify(data.body, null, 2))
      }
      if (key === 'create' && data.docEntry !== undefined && data.docNum !== undefined) {
        setDocEntry(String(data.docEntry))
        setDocNum(String(data.docNum))
      }
      if (key === 'save-back' && data.ok) {
        onExportComplete?.()
      }
    } catch (err) {
      const axErr = err as { response?: { data?: unknown }; message?: string }
      updateStep(key, {
        pending: 'error',
        result: {
          step: key,
          error: axErr?.message ?? String(err),
          sapResponse: axErr?.response?.data ?? null,
        },
      })
    }
  }

  function reset() {
    setSteps(INITIAL_STEPS.map((s) => ({ ...s, pending: 'idle', result: undefined })))
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title="Step-Debug: SAP-Übergabe"
      description="Jeder API-Schritt wird einzeln und manuell ausgeführt. Request-Body und volle SAP-Response sind sichtbar."
      size="xl"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={reset}>Zurücksetzen</Button>
          <Button variant="secondary" onClick={onClose}>Schließen</Button>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-3 max-h-[75vh] overflow-y-auto">
        <div className="rounded-md border border-brand-200 bg-brand-50 p-2 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-brand-700" />
          <p className="text-small text-brand-900">
            <span className="font-medium">Ziel-Mandant:</span>{' '}
            {activeTenant ? `${activeTenant.code}${activeTenant.name ? ` · ${activeTenant.name}` : ''}` : 'kein Mandant aktiv'}
          </p>
          <p className="ml-auto text-caption text-brand-700">Alle Requests gehen an SAP dieses Mandanten</p>
        </div>
        {steps.map((s) => (
          <StepCard
            key={s.key}
            step={s}
            createBody={createBody}
            docEntry={docEntry}
            docNum={docNum}
            onCreateBodyChange={setCreateBody}
            onDocEntryChange={setDocEntry}
            onDocNumChange={setDocNum}
            onRun={() => runStep(s.key)}
          />
        ))}
      </div>
    </Modal>
  )
}

function StepCard({
  step,
  createBody,
  docEntry,
  docNum,
  onCreateBodyChange,
  onDocEntryChange,
  onDocNumChange,
  onRun,
}: {
  step: StepRow
  createBody: string
  docEntry: string
  docNum: string
  onCreateBodyChange: (v: string) => void
  onDocEntryChange: (v: string) => void
  onDocNumChange: (v: string) => void
  onRun: () => void
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          {step.pending === 'idle' && <ChevronRight className="w-4 h-4 text-gray-400" />}
          {step.pending === 'running' && <Loader2 className="w-4 h-4 text-brand-600 animate-spin" />}
          {step.pending === 'done' && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
          {step.pending === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-small font-medium text-gray-900">{step.label}</p>
              <p className="text-caption text-gray-500">{step.description}</p>
            </div>
            <Button variant={step.pending === 'idle' ? 'primary' : 'secondary'} size="sm" onClick={onRun} loading={step.pending === 'running'}>
              Ausführen
            </Button>
          </div>

          {step.key === 'create' && (
            <div className="mt-2">
              <label className="text-caption text-gray-600 block mb-0.5">Request-Body (JSON, editierbar)</label>
              <textarea
                value={createBody}
                onChange={(e) => onCreateBodyChange(e.currentTarget.value)}
                rows={10}
                className="w-full font-mono text-caption border border-gray-200 rounded p-2"
                spellCheck={false}
              />
            </div>
          )}

          {step.key === 'save-back' && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="text-caption text-gray-600 block mb-0.5">DocEntry</label>
                <input
                  className="w-full font-mono text-caption border border-gray-200 rounded p-1.5"
                  value={docEntry}
                  onChange={(e) => onDocEntryChange(e.currentTarget.value)}
                />
              </div>
              <div>
                <label className="text-caption text-gray-600 block mb-0.5">DocNum</label>
                <input
                  className="w-full font-mono text-caption border border-gray-200 rounded p-1.5"
                  value={docNum}
                  onChange={(e) => onDocNumChange(e.currentTarget.value)}
                />
              </div>
            </div>
          )}

          {step.result && (
            <div className="mt-2 space-y-1.5">
              {(step.result.error || (step.result.errors && step.result.errors.length > 0)) ? (
                <div className="rounded border border-red-200 bg-red-50 p-2">
                  <p className="text-caption font-medium text-red-900">Fehler / Validierungs-Probleme</p>
                  {step.result.error && <p className="text-small text-red-900">{step.result.error}</p>}
                  {step.result.httpStatus !== undefined && step.result.httpStatus !== null && (
                    <p className="text-caption text-red-700 mt-0.5">HTTP {step.result.httpStatus}</p>
                  )}
                  {step.result.errors && step.result.errors.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-small text-red-900">
                      {step.result.errors.map((e, i) => <li key={i}>• [{e.field}] {e.message}</li>)}
                    </ul>
                  )}
                  {step.result.sapResponse !== undefined && step.result.sapResponse !== null && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-caption text-red-700">SAP-Originalantwort</summary>
                      <pre className="mt-1 p-2 bg-white border border-red-200 rounded text-caption font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(step.result.sapResponse, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ) : (
                <details className="rounded border border-emerald-200 bg-emerald-50 p-2" open>
                  <summary className="cursor-pointer text-caption font-medium text-emerald-900">Erfolg</summary>
                  {step.result.body && (
                    <div className="mt-1">
                      <p className="text-caption text-emerald-900">Berechneter Body</p>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
                        {JSON.stringify(step.result.body, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.result.docNum !== undefined && step.result.docEntry !== undefined && (
                    <p className="mt-1 text-small text-emerald-900">
                      Angelegt als <strong>QU-{step.result.docNum}</strong> (DocEntry {step.result.docEntry}).
                    </p>
                  )}
                  {step.result.response !== undefined && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-caption text-emerald-700">SAP-Response</summary>
                      <pre className="mt-0.5 p-1.5 bg-white border border-emerald-200 rounded text-caption font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
                        {JSON.stringify(step.result.response, null, 2)}
                      </pre>
                    </details>
                  )}
                  {step.result.ok !== undefined && (
                    <p className="mt-1 text-small text-emerald-900">Lokal gespeichert.</p>
                  )}
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
